/**
 * Created by neo on 23.08.21.
 */
import { makeAutoObservable } from 'mobx';
import { v4 as UUID } from 'uuid';
import { LocalizedValue, createLocalizedValue } from '../../../LocalizedValue';
import { WorkoutDayConfiguration, createWorkoutDayConfiguration } from './WorkoutDayConfiguration';
import { Media, createMedia } from '../../../Media/Media';
import { LocalizedEntity, createLocalizedEntity } from '../../../LocalizedEntity';

export type MesoCycleTemplate = LocalizedEntity & {
  type: string;
  templateName?: string;
  name: LocalizedValue[];
  description: LocalizedValue[];
  image?: Media;
  videos: Media[];
  workoutDays: WorkoutDayConfiguration[];
  cycles: number;
  readonly minWorkoutDurationInMinutes: number | undefined;
  readonly maxWorkoutDurationInMinutes: number | undefined;
  toJS(newId?: boolean): any;
};

export function createMesoCycleTemplate(json?: Partial<MesoCycleTemplate>): MesoCycleTemplate {
  const base = createLocalizedEntity(json as any);
  return makeAutoObservable({
    ...base,
    type: json?.type ?? 'hypertrophy_1',
    templateName: json?.templateName,
    name: ((json?.name as any) ?? []).map((l: any) => createLocalizedValue(l)),
    description: ((json?.description as any) ?? []).map((l: any) => createLocalizedValue(l)),
    image: json?.image ? createMedia(json.image as any) : undefined,
    videos: ((json?.videos as any) ?? []).map((v: any) => createMedia(v)),
    workoutDays: ((json?.workoutDays as any) ?? []).map((d: any) => createWorkoutDayConfiguration(d)),
    cycles: json?.cycles ?? 4,

    get minWorkoutDurationInMinutes(): number | undefined {
      return this.workoutDays.reduce(
        (agg, cycle) =>
          cycle.minWorkoutDurationInMinutes
            ? Math.min(cycle.minWorkoutDurationInMinutes, agg ?? Number.MAX_SAFE_INTEGER)
            : agg,
        undefined as number | undefined,
      );
    },

    get maxWorkoutDurationInMinutes(): number | undefined {
      return this.workoutDays.reduce(
        (agg, cycle) =>
          cycle.maxWorkoutDurationInMinutes
            ? Math.max(cycle.maxWorkoutDurationInMinutes, agg ?? Number.MIN_SAFE_INTEGER)
            : agg,
        undefined as number | undefined,
      );
    },

    toJS(newId: boolean = false) {
      return {
        ...(function() { const { id, createdAt, updatedAt, createdBy, updatedBy, name, description } = base; return { id: newId ? UUID() : id, createdAt, updatedAt, createdBy, updatedBy, name, description }; })(),
        id: newId ? UUID() : this.id,
        type: this.type,
        templateName: this.templateName,
        name: this.name.map((n) => n),
        description: this.description.map((d) => d),
        image: this.image,
        videos: this.videos.map((v) => v),
        workoutDays: this.workoutDays.map((d) => d.toJS(newId)),
        cycles: this.cycles,
      };
    },
  });
}
